import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import { nowPartialDate, subtractYears } from 'neuro-utils';
import colors from '../../../../../../config/theme/colors';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TEpilepsyESSurgicalTreatmentType } from '../../definitions/epilepsy';

const operationTypeOptions = [
  'localResection',
  'lesionectomy',
  'disconnection',
  'cyberKnife',
  'thermoCoagulation',
  'intracranialImaging',
];

const SurgicalTreatmentField = ({ searchFields, onChange }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const current: TEpilepsyESSurgicalTreatmentType =
    searchFields.surgicalTreatment !== 'noCropping' ? searchFields.surgicalTreatment : {};
  const status = current.status;
  const statusSelected = !!status;
  const doneSelected = status === 'done';

  const onChangeStatus = (values: TOnChangeValues) => {
    const newStatus = values['status'] as 'done' | 'notDone' | null;
    if (!newStatus) {
      onChange({ surgicalTreatment: 'noCropping' });
    } else if (newStatus !== 'done') {
      // Switching to notDone — strip done-specific fields
      const { dateFrom, dateTo, engelClassificationAtOneYear, ...rest } = current;
      void dateFrom;
      void dateTo;
      void engelClassificationAtOneYear;
      onChange({ surgicalTreatment: { ...rest, status: newStatus } });
    } else {
      onChange({ surgicalTreatment: { ...current, status: newStatus } });
    }
  };

  const onChangeOperationType = (values: TOnChangeValues) => {
    const newTypes = (values['operationType'] as string[]) ?? [];
    onChange({ surgicalTreatment: { ...current, operationType: newTypes.length > 0 ? newTypes : undefined } });
  };

  const onChangeDateFrom = (values: TOnChangeValues) => {
    onChange({ surgicalTreatment: { ...current, dateFrom: (values['dateFrom'] as PartialDate) ?? undefined } });
  };

  const onChangeDateTo = (values: TOnChangeValues) => {
    onChange({ surgicalTreatment: { ...current, dateTo: (values['dateTo'] as PartialDate) ?? undefined } });
  };

  const onChangeEngel = (values: TOnChangeValues) => {
    const checked = values['engelClassificationAtOneYear'] !== null;
    if (checked) {
      onChange({ surgicalTreatment: { ...current, engelClassificationAtOneYear: true } });
    } else {
      const { engelClassificationAtOneYear, ...rest } = current;
      void engelClassificationAtOneYear;
      onChange({ surgicalTreatment: rest });
    }
  };

  return (
    <React.Fragment>
      <InputHandler
        type="Radio"
        editing={true}
        name="status"
        formData={{
          onChange: onChangeStatus,
          document: { status: status ?? null },
        }}
        options={['done', 'notDone']}
        optionFormatter={(name: string | number) => fm(`extendedSearch.opts.${name}`)}
      />
      {statusSelected && (
        <React.Fragment>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('surgicalTreatment.operation.typeOfOperation.title')}
          </div>
          <InputHandler
            type="Checkbox"
            editing={true}
            name="operationType"
            formData={{
              onChange: onChangeOperationType,
              document: { operationType: current.operationType ?? [] },
            }}
            options={operationTypeOptions}
            optionFormatter={(name: string | number) => fm(`surgicalTreatment.operation.typeOfOperation.opts.${name}`)}
          />
        </React.Fragment>
      )}
      {doneSelected && (
        <React.Fragment>
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.minSurgicalTreatmentDate')}
          </div>
          <InputHandler
            type="PartialDate"
            editing={true}
            name="dateFrom"
            formData={{
              onChange: onChangeDateFrom,
              document: { dateFrom: current.dateFrom ?? null },
            }}
            dateHook={{
              dateHookFloor: subtractYears(nowPartialDate(), 100),
              dateHookCeiling: current.dateTo ?? undefined,
            }}
          />
          <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 0.5rem 0' }}>
            {fm('extendedSearch.maxSurgicalTreatmentDate')}
          </div>
          <InputHandler
            type="PartialDate"
            editing={true}
            name="dateTo"
            formData={{
              onChange: onChangeDateTo,
              document: { dateTo: current.dateTo ?? null },
            }}
            dateHook={{
              dateHookFloor: current.dateFrom ?? subtractYears(nowPartialDate(), 100),
            }}
          />
          <div style={{ margin: '2rem 0 0 0' }}>
            <InputHandler
              type="CheckboxSingle"
              editing={true}
              name="engelClassificationAtOneYear"
              formData={{
                onChange: onChangeEngel,
                document: {
                  engelClassificationAtOneYear: current.engelClassificationAtOneYear ? true : null,
                },
              }}
              option="true"
              optionFormatter={() => fm('extendedSearch.opts.engelClassificationAtOneYear')}
              showLabel
            />
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'searchFields' | 'onChange'> {}

export default SurgicalTreatmentField;
